import { apis } from 'apis/apis';
import { ISearchParams } from 'apis/search_apis';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import useCommonStore from 'store/useCommonStore';
import { ISearchDB } from 'type/db';
import { getCookie } from 'utils/cookies';

interface ISearchExclude {
	ig_exclude?: string;
	yt_exclude?: string;
	nb_exclude?: string;
	tt_exclude?: string;
}

export const useGetSearchRecommendQuery = <T>(keyword: string, options?: T) => {
	return useQuery([`search_recommend`, keyword], () => apis.getSearchRecommend(keyword), {
		retry: 0,
		retryDelay: 1000 * 10,
		onError: (err) => {
			console.log(err);
		},
		enabled: getCookie('imms_workspace_code') ? true : false,
		staleTime: 0,
		cacheTime: Infinity,
		...options,
	});
};

export const useGetSearchQuery = <T>(keyword: string, options?: T) => {
	const { search_platform, search_filter } = useCommonStore();
	const queryClient = useQueryClient();

	const params = {
		keyword: keyword,
		page_size: '10',
		platform_code: search_platform.code,
	};

	// 키워드와 일치하는 검색결과 데이터
	const search_exact_data: { results: ISearchDB[] } | undefined =
		queryClient.getQueryData(['search_exact', new URLSearchParams(params).toString()]);

	let exclude_params: ISearchExclude = {};

	// 검색 결과 값에서 pk값만 플랫폼 별로 추출
	const ig_exclude: string[] = [];
	const yt_exclude: string[] = [];
	const nb_exclude: string[] = [];
	const tt_exclude: string[] = [];

	search_exact_data?.results.forEach((item: ISearchDB) => {
		if (item.platform_code === 'ig') ig_exclude.push(item.platform_pk);
		if (item.platform_code === 'yt') yt_exclude.push(item.platform_pk);
		if (item.platform_code === 'nb') nb_exclude.push(item.platform_pk);
		if (item.platform_code === 'tt') tt_exclude.push(item.platform_pk);
	});

	if (ig_exclude.length !== 0) exclude_params.ig_exclude = ig_exclude.join(',');
	if (yt_exclude.length !== 0) exclude_params.yt_exclude = yt_exclude.join(',');
	if (nb_exclude.length !== 0) exclude_params.nb_exclude = nb_exclude.join(',');
	if (tt_exclude.length !== 0) exclude_params.tt_exclude = tt_exclude.join(',');

	if (search_filter.code !== 'total') exclude_params = {};

	// 키워드와 일치하는 데이터를 포함한 params
	const exclude_added_params: ISearchParams = {
		...params,
		...exclude_params,
	};

	if (search_filter.code !== 'code')
		exclude_added_params.division = search_filter.code as 'username' | 'etc';

	return useInfiniteQuery(
		[`search`, new URLSearchParams(params).toString(), search_filter.code],
		({ pageParam = 1 }) => apis.getSearch(pageParam, exclude_added_params),
		{
			retry: 0,
			onError: (err) => {
				console.log(err);
			},
			getNextPageParam: (last_page, pages) => {
				return last_page?.results.length > 1 ? pages.length + 1 : false;
			},
			enabled:
				!getCookie('imms_workspace_code') ||
				search_exact_data === undefined ||
				keyword === undefined ||
				keyword === '' ||
				keyword.trim() === ''
					? false
					: true,
			staleTime: 1000 * 60 * 60,
			cacheTime: Infinity,
			...options,
		},
	);
};

export const useGetExactSearchQuery = <T>(keyword: string, options?: T) => {
	const { search_platform } = useCommonStore();

	const params = {
		keyword: keyword,
		page_size: '10',
		platform_code: search_platform.code,
	};

	const exact_option_added_params = {
		...params,
		exact_username: 'true',
	};

	return useQuery(
		[`search_exact`, new URLSearchParams(params).toString()],
		() => apis.getSearch(1, exact_option_added_params),
		{
			retry: 0,
			onError: (err) => {
				console.log(err);
			},
			enabled:
				!getCookie('imms_workspace_code') ||
				keyword === undefined ||
				keyword.trim() === ''
					? false
					: true,
			staleTime: 1000 * 60 * 60,
			cacheTime: Infinity,
			...options,
		},
	);
};
